import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Input,
  useTableFeatures,
  useTableSort,
  createTableColumn,
  Tooltip,
} from "@fluentui/react-components";
import { CiFilter } from "react-icons/ci";
import { DeleteRegular, Add20Filled, EditRegular, EyeRegular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import "../CSS/ListWorkflowsComponent.css";
import { getAccessToken } from "../../Services/GraphServices";
import { deleteWorkflow } from "../../Services/HeedifyServices";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import { useNavigate } from "react-router-dom";
import { useAbility } from "../../Contexts/AbilityContext";
import InfoButton from "../GenericComponent/InfoButton";
import { useNotification } from '../GenericComponent/NotificationSystem';

const ListWorkflowsComponent = (props) => {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ability = useAbility();
  const { showSuccess, showError } = useNotification();

  const [workflowsRows, setWorkflowsRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [filterVisible, setFilterVisible] = useState({
    DISPLAY_NAME: false,
    TRIGGER_EVENT: false,
    SCOPE: false,
    ACTION_METHOD: false,
    ACTION_URL: false,
  });
  const [filters, setFilters] = useState({
    DISPLAY_NAME: "",
    TRIGGER_EVENT: "",
    SCOPE: "",
    ACTION_METHOD: "",
    ACTION_URL: "",
  });
  
  useEffect(() => {
    if (props.WORKFLOWS) {
      // 🔐 Calculer permissions pour chaque workflow
      const workflowsWithPermissions = props.WORKFLOWS.map((workflow) => ({
        ...workflow,
        permissions: {
          main: {
            canCreate: ability?.can("create", `Workflows.main@${workflow.scope}`),
            canRead: ability?.can("read", `Workflows.main@${workflow.scope}`),
            canUpdate: ability?.can("update", `Workflows.main@${workflow.scope}`),
            canDelete: ability?.can("delete", `Workflows.main@${workflow.scope}`),
          },
        },
      }));
      setWorkflowsRows(workflowsWithPermissions);
      setFilteredRows(workflowsWithPermissions);
    }
  }, [props.WORKFLOWS, ability]);

  // --- Fonction de filtrage externe ---
  const applyFilters = (workflowsRows, filters) => {
    return workflowsRows.filter((workflow) => {
      const matchDisplayName = (workflow.displayName || "")
        .toLowerCase()
        .includes(filters.DISPLAY_NAME.toLowerCase());
      const matchTrigger = workflow.triggerEvent
        ?.toLowerCase()
        .includes(filters.TRIGGER_EVENT.toLowerCase());
      const matchScope = workflow.scope
        ?.toLowerCase()
        .includes(filters.SCOPE.toLowerCase());
      const matchActionMethod = (workflow.action?.method || "")
        .toLowerCase()
        .includes(filters.ACTION_METHOD.toLowerCase());
      const matchActionUrl = (workflow.action?.url || "")
        .toLowerCase()
        .includes(filters.ACTION_URL.toLowerCase());
      // 🔐 Filtrer par permission canRead
      const hasReadPermission = workflow.permissions?.main?.canRead;

      return matchDisplayName && matchTrigger && matchScope && matchActionMethod && matchActionUrl && hasReadPermission;
    });
  };

  // --- useEffect qui applique le filtre ---
  useEffect(() => {
    const newFiltered = applyFilters(workflowsRows, filters);
    setFilteredRows(newFiltered);
  }, [filters, workflowsRows]);

  // --- Tri avec Sorting ---
  const columns = [
    createTableColumn({
      columnId: "DISPLAY_NAME",
      compare: (a, b) => (a.displayName || "").localeCompare(b.displayName || ""),
    }),
    createTableColumn({
      columnId: "TRIGGER_EVENT",
      compare: (a, b) => (a.triggerEvent || "").localeCompare(b.triggerEvent || ""),
    }),
    createTableColumn({
      columnId: "SCOPE",
      compare: (a, b) => (a.scope || "").localeCompare(b.scope || ""),
    }),
    createTableColumn({
      columnId: "ACTION_METHOD",
      compare: (a, b) => (a.action?.method || "").localeCompare(b.action?.method || ""),
    }),
    createTableColumn({
      columnId: "ACTION_URL",
      compare: (a, b) => (a.action?.url || "").localeCompare(b.action?.url || ""),
    }),
    createTableColumn({ columnId: "ACTION" }),
  ];

  const {
    getRows,
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures({ columns, items: filteredRows }, [
    useTableSort({
      defaultSortState: { sortColumn: "TRIGGER_EVENT", sortDirection: "ascending" },
    }),
  ]);

  const rows = useMemo(() => {
    return sort(getRows());
  }, [sort, getRows]);

  const headerSortProps = (columnId) => ({
    onClick: (e) => toggleColumnSort(e, columnId),
    sortDirection: getSortDirection(columnId),
  });

  const handleDelete = async () => {
    if (!selectedWorkflow) return;

    try {
      const token = await getAccessToken(instance, activeAccount);
      const result = await deleteWorkflow(
        props.ACTIVE_TENANT,
        selectedWorkflow.scope,
        selectedWorkflow.id,
        token
      );

      if (result.success) {
        // Mettre à jour le store Redux
        if (props.setWorkflows && props.WORKFLOWS) {
          props.setWorkflows(props.WORKFLOWS.filter((w) => w.id !== selectedWorkflow.id));
        }
        setWorkflowsRows((prev) => prev.filter((w) => w.id !== selectedWorkflow.id));
        setSelectedWorkflow(null);
        showSuccess(t("WORKFLOW_DELETED_SUCCESS"));
      } else {
        const errorMsg = result.message || "Erreur inconnue";
        showError(t("WORKFLOW_DELETE_FAILED"), errorMsg);
      }
    } catch (error) {
      console.error("Error deleting workflow:", error);
      showError(t("WORKFLOW_DELETE_ERROR"), error?.message);
    }
  };

  const handleFilterChange = (columnKey, value) => {
    setFilters((prev) => ({ ...prev, [columnKey]: value }));
  };

  // Toggle the visibility of a filter input field
  const toggleFilterVisibility = (column) => {
    setFilterVisible((prev) => ({ ...prev, [column]: !prev[column] }));
  };

  const hasCreatePermission = props.SCOPES_NAMES?.some((scope) =>
    ability?.can("create", `Workflows.main@${scope}`)
  );

  return (
    <div className="list-workflows-container">
      <div className="header-section">
        <h2>{t("WORKFLOWS_LIST")}</h2>
        <div className="header-actions">
          <InfoButton infoKey="workflows" />
          {hasCreatePermission && (
            <Button
              appearance="primary"
              icon={<Add20Filled />}
              onClick={() => navigate("/workflows/add")}
            >
              {t("ADD_NEW_WORKFLOW")}
            </Button>
          )}
        </div>
      </div>

      <div className="table-container">
        <div className="workflow-list-table-wrapper">
          <Table
            role="grid"
            aria-label="Workflows table with filters and sorting"
            className="workflow-list-table-style"
            sortable
          >
            <TableHeader>
              {/* Ligne des titres triables */}
              <TableRow className="workflow-list-header-style">
                <TableHeaderCell className="workflow-list-cell-bold" {...headerSortProps("DISPLAY_NAME")}>
                  <div className="header-filter-container">
                    <div className="div-filter-style">
                  {t("NAME")}
                      <CiFilter
                        className="filter-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFilterVisibility("DISPLAY_NAME");
                        }}
                      />
                    </div>
                    {filterVisible.DISPLAY_NAME && (
                      <Input
                        className="input-filter-style"
                        value={filters.DISPLAY_NAME}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e, data) =>
                          handleFilterChange("DISPLAY_NAME", data.value)
                        }
                        placeholder={t("FILTER")}
                        size="small"
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell className="workflow-list-cell-bold" {...headerSortProps("TRIGGER_EVENT")}>
                  <div className="header-filter-container">
                    <div className="div-filter-style">
                      {t("TRIGGER_EVENT")}
                      <InfoButton infoKey="TRIGGER_EVENT" category="WORKFLOWS" />
                      <CiFilter
                        className="filter-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFilterVisibility("TRIGGER_EVENT");
                        }}
                      />
                    </div>
                    {filterVisible.TRIGGER_EVENT && (
                      <Input
                        className="input-filter-style"
                        value={filters.TRIGGER_EVENT}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e, data) =>
                          handleFilterChange("TRIGGER_EVENT", data.value)
                        }
                        placeholder={t("FILTER")}
                        size="small"
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell className="workflow-list-cell-bold" {...headerSortProps("SCOPE")}>
                  <div className="header-filter-container">
                    <div className="div-filter-style">
                      {t("SCOPE")}
                      <CiFilter
                        className="filter-icon"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleFilterVisibility("SCOPE");
                        }}
                      />
                    </div>
                    {filterVisible.SCOPE && (
                      <Input
                        className="input-filter-style"
                        value={filters.SCOPE}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e, data) =>
                          handleFilterChange("SCOPE", data.value)
                        }
                        placeholder={t("FILTER")}
                        size="small"
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell className="workflow-list-cell-bold" {...headerSortProps("ACTION_METHOD")}>
                  <div className="header-filter-container">
                    <div className="div-filter-style">
                      {t("METHOD")}
                      <CiFilter
                        className="filter-icon"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleFilterVisibility("ACTION_METHOD");
                        }}
                      />
                    </div>
                    {filterVisible.ACTION_METHOD && (
                      <Input
                        className="input-filter-style"
                        value={filters.ACTION_METHOD}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e, data) =>
                          handleFilterChange("ACTION_METHOD", data.value)
                        }
                        placeholder={t("FILTER")}
                        size="small"
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell className="workflow-list-cell-bold" {...headerSortProps("ACTION_URL")}>
                  <div className="header-filter-container">
                    <div className="div-filter-style">
                      {t("URL")}
                      <CiFilter
                        className="filter-icon"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleFilterVisibility("ACTION_URL");
                        }}
                      />
                    </div>
                    {filterVisible.ACTION_URL && (
                      <Input
                        className="input-filter-style"
                        value={filters.ACTION_URL}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e, data) =>
                          handleFilterChange("ACTION_URL", data.value)
                        }
                        placeholder={t("FILTER")}
                        size="small"
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell className="workflow-list-cell-bold agent-profile-list-cell-right">{t("ACTION")}</TableHeaderCell>
              </TableRow>
            </TableHeader>

            <TableBody>
              {rows.map(({ item, rowId }) => (
                <TableRow key={rowId}>
                  <TableCell>
                    <TableCellLayout>{item.displayName || item.id}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>{item.triggerEvent.toLowerCase().replace(/_/g, ' ') }</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>{item.scope }</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>{item.action?.method || "POST"}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>
                      {item.action?.url && (
                        <Tooltip content={item.action.url} relationship="label">
                          <span>
                            {item.action.url.substring(0, 20)}
                            {item.action.url.length > 20 ? "..." : ""}
                          </span>
                        </Tooltip>
                      )}
                    </TableCellLayout>
                  </TableCell>
                  <TableCell className="workflow-list-cell-right">
                    {/* Ordre constant dans tout le portail : CONSULTER à gauche, SUPPRIMER à
                        droite. Le geste destructeur est le plus éloigné du geste courant, et il
                        se trouve toujours au même endroit d'un écran à l'autre. */}
                    <Button
                      appearance="subtle"
                      icon={item.permissions?.main?.canUpdate ? <EditRegular /> : <EyeRegular />}
                      aria-label={item.permissions?.main?.canUpdate ? "Edit" : "View"}
                      onClick={() => navigate(`/workflows/${item.id}`, { state: { workflow: item } })}
                      disabled={!item.permissions?.main?.canRead}
                    />
                    <Button
                      appearance="subtle"
                      icon={<DeleteRegular />}
                      aria-label="Delete"
                      onClick={() => setSelectedWorkflow(item)}
                      disabled={!item.permissions?.main?.canDelete}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {selectedWorkflow && (
        <ConfirmAlert
          open={!!selectedWorkflow}
          name={selectedWorkflow.displayName}
          onConfirm={handleDelete}
          onCancel={() => setSelectedWorkflow(null)}
        />
      )}
    </div>
  );
};

export default ListWorkflowsComponent;
